import React, { useState } from 'react';
import Toast from './Toast';
import { sendAnalyticsReport } from '../services/activityService';
import './EmailReportModal.css';

// ── Config — update MAX_EMAILS here if limits change ──────────
const MAX_EMAILS = 5;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EmailReportModal = ({ onClose, summaries = [] }) => {
  const [emailInputs, setEmailInputs] = useState(['']);
  const [isSending, setIsSending]     = useState(false);
  const [toast, setToast]             = useState(null);

  // ── CSV builder ────────────────────────────────────────────
  const buildCsv = () => {
    const header = 'Student,Attempts,Correct,Accuracy (%),Avg Voice Score (%),Daily Living Scenarios Completed,Last Active';
    const rows = summaries.map((s) => {
      const accuracy = s.total === 0 ? '—' : Math.round((s.correct / s.total) * 100);
      const voice    = s.avgVoiceScore != null ? s.avgVoiceScore : '—';
      const last     = s.lastActive ?? 'No activity';
      return `"${s.name}",${s.total},${s.correct},${accuracy},${voice},${s.scenariosCompleted || 0},"${last}"`;
    });
    return [header, ...rows].join('\n');
  };

  // ── Email list management ──────────────────────────────────
  const updateEmail = (idx, value) => {
    setEmailInputs((prev) => prev.map((e, i) => (i === idx ? value : e)));
  };

  const addEmail = () => {
    if (emailInputs.length < MAX_EMAILS) {
      setEmailInputs((prev) => [...prev, '']);
    }
  };

  const removeEmail = (idx) => {
    setEmailInputs((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── Send ───────────────────────────────────────────────────
  const handleSend = async () => {
    const validEmails = emailInputs
      .map((e) => e.trim())
      .filter((e) => EMAIL_REGEX.test(e));

    if (validEmails.length === 0) {
      setToast({ message: 'Please enter at least one valid email address.', type: 'error' });
      return;
    }

    setIsSending(true);
    const { error } = await sendAnalyticsReport(validEmails, buildCsv());
    setIsSending(false);

    if (error) {
      setToast({ message: 'Failed to send report. Please try again.', type: 'error' });
    } else {
      setToast({ message: `Report sent to ${validEmails.length} recipient(s)! ✅`, type: 'success' });
      setTimeout(() => onClose(), 2000);
    }
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-labelledby="email-modal-title">
      <div className="modal-card email-modal">

        {/* Header */}
        <div className="email-modal-header">
          <i className="fas fa-envelope-open-text" aria-hidden="true"></i>
          <div>
            <h2 id="email-modal-title">Email Analytics Report</h2>
            <p>Send a CSV report to up to {MAX_EMAILS} recipients</p>
          </div>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        </div>

        {/* Email inputs */}
        <div className="email-inputs-list">
          {emailInputs.map((email, idx) => (
            <div key={idx} className="email-input-row">
              <i className="fas fa-envelope email-icon" aria-hidden="true"></i>
              <input
                type="email"
                className="form-control"
                placeholder={`Recipient ${idx + 1} email`}
                value={email}
                onChange={(e) => updateEmail(idx, e.target.value)}
                aria-label={`Email recipient ${idx + 1}`}
              />
              {emailInputs.length > 1 && (
                <button
                  className="remove-email-btn"
                  onClick={() => removeEmail(idx)}
                  aria-label={`Remove recipient ${idx + 1}`}
                >
                  <i className="fas fa-times" aria-hidden="true"></i>
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Add email button */}
        {emailInputs.length < MAX_EMAILS && (
          <button className="add-email-btn" onClick={addEmail}>
            <i className="fas fa-plus" aria-hidden="true"></i>
            Add another recipient ({emailInputs.length}/{MAX_EMAILS})
          </button>
        )}

        {emailInputs.length >= MAX_EMAILS && (
          <p className="email-limit-notice">
            <i className="fas fa-info-circle" aria-hidden="true"></i>
            Maximum {MAX_EMAILS} recipients reached.
          </p>
        )}

        {/* Actions */}
        <div className="email-modal-actions">
          <button className="btn-modal-cancel" onClick={onClose} disabled={isSending}>
            Cancel
          </button>
          <button className="btn-modal-send" onClick={handleSend} disabled={isSending}>
            {isSending ? (
              <><i className="fas fa-spinner fa-spin" aria-hidden="true"></i> Sending…</>
            ) : (
              <><i className="fas fa-paper-plane" aria-hidden="true"></i> Send Report</>
            )}
          </button>
        </div>

        {/* Toast */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onDismiss={() => setToast(null)}
          />
        )}
      </div>
    </div>
  );
};

export default EmailReportModal;
